const configElement = document.getElementById('CaleoFreeGiftV2Config');

if (configElement) {
  const config = JSON.parse(configElement.textContent);
  const products = config.products.filter(
    (product) => product && product.id && product.variants?.some((variant) => variant.available !== false)
  );
  const giftProductIds = new Set(products.map((product) => Number(product.id)));
  const giftVariantIds = new Set(
    products.flatMap((product) => product.variants || []).map((variant) => Number(variant.id))
  );
  const preferredVariantByProduct = new Map();
  const giftProperty = '_caleo_free_gift';
  const minimumItems = Math.max(2, Number(config.minimum_items) || 2);
  let isMutating = false;
  let refreshTimer;

  const moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD',
  });

  function isGiftLine(item) {
    return (
      item.properties?.[giftProperty] === 'true' ||
      giftProductIds.has(Number(item.product_id))
    );
  }

  function getGiftLines(cart) {
    return cart.items.filter(isGiftLine);
  }

  function qualifyingItemCount(cart) {
    return cart.items.reduce(
      (count, item) => count + (isGiftLine(item) ? 0 : Math.max(0, Number(item.quantity) || 0)),
      0
    );
  }

  function isEligible(cart) {
    return qualifyingItemCount(cart) >= minimumItems;
  }

  function availableVariants(product) {
    return (product.variants || []).filter((variant) => variant.available !== false);
  }

  function getProductByVariantId(variantId) {
    return products.find((product) =>
      availableVariants(product).some((variant) => Number(variant.id) === Number(variantId))
    );
  }

  function getVariant(product, variantId) {
    return availableVariants(product).find((variant) => Number(variant.id) === Number(variantId));
  }

  function preferredVariant(product) {
    return (
      getVariant(product, preferredVariantByProduct.get(Number(product.id))) || availableVariants(product)[0]
    );
  }

  function variantImage(variant, product) {
    return variant.featured_image?.src || product.featured_image || '';
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to refresh cart.');
    return response.json();
  }

  async function postCart(url, payload) {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || data.errors || 'Unable to update cart.');
    }
    return data;
  }

  function announceCartRefresh() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function getMountPoint() {
    const root = document.getElementById('rapi-cart-drawer-app');
    const body = root?.querySelector('[data-rapi-cart-body], .rapi-cart-drawer__body');
    return { root, body };
  }

  function setCheckoutDisabled(disabled) {
    const checkout = document.querySelector('#rapi-cart-drawer-app [data-rapi-checkout]');
    if (!(checkout instanceof HTMLButtonElement)) return;
    checkout.disabled = disabled;
    checkout.setAttribute('aria-disabled', disabled.toString());
  }

  function createElement(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  function showStatus(selector, message, isError = false) {
    const status = selector?.querySelector('[data-caleo-gift-status]');
    if (!status) return;
    status.textContent = message;
    status.setAttribute('role', isError ? 'alert' : 'status');
  }

  function setBusy(selector, busy) {
    selector?.classList.toggle('is-updating', busy);
    selector?.setAttribute('aria-busy', busy.toString());
    selector?.querySelectorAll('button, select').forEach((control) => {
      control.disabled = busy || selector.classList.contains('is-locked');
    });
    setCheckoutDisabled(busy);
  }

  function updateVisualSelection(selector, product, variant, currentGiftLines) {
    preferredVariantByProduct.set(Number(product.id), Number(variant.id));

    const image = selector.querySelector('[data-caleo-gift-main-image]');
    if (image instanceof HTMLImageElement) {
      image.src = variantImage(variant, product);
      image.alt = product.title;
    }

    const title = selector.querySelector('[data-caleo-gift-title]');
    if (title) title.textContent = product.title;

    const oldPrice = selector.querySelector('[data-caleo-gift-old-price]');
    if (oldPrice) oldPrice.textContent = moneyFormatter.format(Number(variant.price || 0) / 100);

    selector.querySelectorAll('[data-caleo-gift-product]').forEach((button) => {
      const selected = Number(button.dataset.productId) === Number(product.id);
      button.setAttribute('aria-pressed', selected.toString());
      if (selected) {
        const thumbnail = button.querySelector('img');
        if (thumbnail instanceof HTMLImageElement) thumbnail.src = variantImage(variant, product);
      }
    });

    const unitPrice = selector.querySelector('.adv__caleo-free-gift-v2__unit-price');
    unitPrice?.setAttribute(
      'aria-label',
      `Free gift price $0. Regular price ${moneyFormatter.format(Number(variant.price || 0) / 100)}`
    );

    const select = selector.querySelector('[data-caleo-gift-variant]');
    if (!(select instanceof HTMLSelectElement)) return;

    select.dataset.productId = String(product.id);
    select.replaceChildren();
    for (const productVariant of availableVariants(product)) {
      const option = createElement('option', '', productVariant.title);
      option.value = String(productVariant.id);
      option.selected = Number(productVariant.id) === Number(variant.id);
      select.append(option);
    }

    select.onchange = () => {
      const selectedVariant = getVariant(product, select.value);
      if (!selectedVariant) return;
      preferredVariantByProduct.set(Number(product.id), Number(selectedVariant.id));
      updateVisualSelection(selector, product, selectedVariant, currentGiftLines);

      if (currentGiftLines.length === 1 && Number(currentGiftLines[0].variant_id) === Number(selectedVariant.id)) {
        return;
      }
      replaceGift(selectedVariant.id, selector, currentGiftLines);
    };
  }

  function renderSelector(cart, optimisticVariantId = 0) {
    const { root, body } = getMountPoint();
    if (!root || !body || products.length === 0) return null;

    const eligible = isEligible(cart);
    root.classList.toggle('caleo-free-gift-eligible', eligible);
    let selector = root.querySelector('[data-caleo-free-gift-v2]');
    if (!selector) {
      selector = createElement('section', 'adv__caleo-free-gift-v2');
      selector.dataset.caleoFreeGiftV2 = '';
      selector.setAttribute('aria-labelledby', 'CaleoFreeGiftV2Heading');
      body.insertAdjacentElement('afterend', selector);
    }
    selector.classList.toggle('is-locked', !eligible);
    selector.setAttribute('aria-disabled', (!eligible).toString());

    const giftLines = getGiftLines(cart);
    const selectedVariantId =
      Number(optimisticVariantId) || (giftLines.length === 1 ? Number(giftLines[0].variant_id) : 0);
    const selectedProduct = getProductByVariantId(selectedVariantId) || products[0];
    const selectedVariant = getVariant(selectedProduct, selectedVariantId) || preferredVariant(selectedProduct);
    preferredVariantByProduct.set(Number(selectedProduct.id), Number(selectedVariant.id));
    selector.replaceChildren();

    const header = createElement('div', 'adv__caleo-free-gift-v2__header');
    const heading = createElement('h3', 'adv__caleo-free-gift-v2__heading');
    heading.id = 'CaleoFreeGiftV2Heading';
    heading.setAttribute('aria-label', config.heading || 'Choose your free gift');
    const giftIcon = createElement('span', 'adv__caleo-free-gift-v2__gift-icon');
    giftIcon.setAttribute('aria-hidden', 'true');
    giftIcon.innerHTML =
      '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M3 9h18v12H3z"/><path d="M12 9v12M2 5h20v4H2z"/><path d="M12 5H8.5a2.5 2.5 0 1 1 2.5-2.5c0 1.4 1 2.5 1 2.5Zm0 0h3.5A2.5 2.5 0 1 0 13 2.5C13 3.9 12 5 12 5Z"/></svg>';
    heading.append(giftIcon, document.createTextNode('Free gift'));

    const unitPrice = createElement('div', 'adv__caleo-free-gift-v2__unit-price');
    unitPrice.setAttribute(
      'aria-label',
      `Free gift price $0. Regular price ${moneyFormatter.format(Number(selectedVariant.price || 0) / 100)}`
    );
    unitPrice.append(
      createElement('span', '', 'Unit price'),
      createElement('strong', '', moneyFormatter.format(0))
    );
    const oldPrice = createElement(
      's',
      '',
      moneyFormatter.format(Number(selectedVariant.price || 0) / 100)
    );
    oldPrice.dataset.caleoGiftOldPrice = '';
    oldPrice.setAttribute('aria-hidden', 'true');
    unitPrice.append(oldPrice);
    header.append(heading, unitPrice);

    const lockedMessage = createElement(
      'p',
      'adv__caleo-free-gift-v2__locked-message',
      config.locked_message || 'Pick at least 2 products to unlock a free gift worth $75.'
    );
    lockedMessage.id = 'CaleoFreeGiftV2LockedMessage';
    lockedMessage.setAttribute('role', 'status');
    const lockIcon = createElement('span', 'adv__caleo-free-gift-v2__lock-icon');
    lockIcon.setAttribute('aria-hidden', 'true');
    lockIcon.innerHTML =
      '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>';
    lockedMessage.prepend(lockIcon);
    if (!eligible) {
      selector.setAttribute('aria-describedby', lockedMessage.id);
    } else {
      selector.removeAttribute('aria-describedby');
    }

    const bodyElement = createElement('div', 'adv__caleo-free-gift-v2__body');
    const mainImage = createElement('img', 'adv__caleo-free-gift-v2__main-image');
    mainImage.dataset.caleoGiftMainImage = '';
    mainImage.src = variantImage(selectedVariant, selectedProduct);
    mainImage.alt = selectedProduct.title;
    mainImage.width = 116;
    mainImage.height = 116;
    mainImage.loading = 'lazy';

    const controls = createElement('div', 'adv__caleo-free-gift-v2__controls');
    const title = createElement('p', 'adv__caleo-free-gift-v2__title', selectedProduct.title);
    title.dataset.caleoGiftTitle = '';

    const switcher = createElement('fieldset', 'adv__caleo-free-gift-v2__switcher');
    switcher.append(createElement('legend', 'adv__caleo-free-gift-v2__legend', 'Choose gift product'));

    for (const product of products) {
      const productVariant =
        Number(product.id) === Number(selectedProduct.id) ? selectedVariant : preferredVariant(product);
      const button = createElement('button', 'adv__caleo-free-gift-v2__product-button');
      button.type = 'button';
      button.dataset.caleoGiftProduct = '';
      button.dataset.productId = String(product.id);
      button.setAttribute('aria-label', `Select ${product.title} as your free gift`);
      button.setAttribute(
        'aria-pressed',
        (Number(product.id) === Number(selectedProduct.id)).toString()
      );

      const thumbnail = createElement('img', 'adv__caleo-free-gift-v2__thumbnail');
      thumbnail.src = variantImage(productVariant, product);
      thumbnail.alt = '';
      thumbnail.width = 44;
      thumbnail.height = 44;
      thumbnail.loading = 'lazy';
      button.append(thumbnail);
      button.addEventListener('click', () => {
        const nextVariant = preferredVariant(product);
        updateVisualSelection(selector, product, nextVariant, giftLines);
        if (giftLines.length === 1 && Number(giftLines[0].variant_id) === Number(nextVariant.id)) return;
        replaceGift(nextVariant.id, selector, giftLines);
      });
      switcher.append(button);
    }

    const selectId = 'CaleoFreeGiftV2Variant';
    const label = createElement('label', 'adv__caleo-free-gift-v2__label', 'Choose variant');
    label.htmlFor = selectId;
    const select = createElement('select', 'adv__caleo-free-gift-v2__select');
    select.id = selectId;
    select.dataset.caleoGiftVariant = '';

    controls.append(title, switcher, label, select);
    bodyElement.append(mainImage, controls);

    const status = createElement('p', 'adv__caleo-free-gift-v2__status');
    status.dataset.caleoGiftStatus = '';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');

    selector.append(header, lockedMessage, bodyElement, status);
    updateVisualSelection(selector, selectedProduct, selectedVariant, giftLines);
    if (!eligible) {
      selector.querySelectorAll('button, select').forEach((control) => {
        control.disabled = true;
      });
    }
    decorateGiftLines(cart);
    return selector;
  }

  function decorateGiftLines(cart) {
    const root = document.getElementById('rapi-cart-drawer-app');
    if (!root) return;

    root.querySelectorAll('.caleo-free-gift-line').forEach((row) => row.classList.remove('caleo-free-gift-line'));
    root.querySelectorAll('.caleo-free-gift-line__badge').forEach((badge) => badge.remove());
    for (const item of getGiftLines(cart)) {
      const keyedControl = root.querySelector(`[data-rapi-line-key="${CSS.escape(item.key)}"]`);
      const row = keyedControl?.closest('.rapi-cart-drawer__item');
      if (!row) continue;
      row.classList.add('caleo-free-gift-line');
      const name = row.querySelector('.rapi-cart-drawer__name');
      if (name) {
        name.insertAdjacentElement(
          'beforebegin',
          createElement('span', 'caleo-free-gift-line__badge', 'Free gift')
        );
      }
    }
  }

  async function replaceGift(variantId, selector, existingGiftLines = []) {
    if (isMutating || !giftVariantIds.has(Number(variantId))) return;
    if (
      existingGiftLines.length === 1 &&
      Number(existingGiftLines[0].variant_id) === Number(variantId) &&
      Number(existingGiftLines[0].quantity) === 1
    ) {
      return;
    }

    isMutating = true;
    setBusy(selector, true);
    showStatus(selector, 'Updating your free gift…');

    try {
      const updates = Object.fromEntries(existingGiftLines.map((item) => [item.key, 0]));
      updates[String(variantId)] = 1;
      await postCart('/cart/update.js', { updates });

      showStatus(selector, 'Free gift updated.');
      announceCartRefresh();
      scheduleRefresh(80);
    } catch (error) {
      showStatus(selector, error instanceof Error ? error.message : 'Unable to update your free gift.', true);
    } finally {
      isMutating = false;
      setBusy(selector, false);
    }
  }

  async function normalizeGiftLines(giftLines) {
    if (isMutating || giftLines.length === 0) return;
    isMutating = true;
    setCheckoutDisabled(true);

    try {
      const validGiftLines = giftLines.filter((item) => giftVariantIds.has(Number(item.variant_id)));
      const giftToKeep = validGiftLines[0];
      const updates = {};

      for (const giftLine of giftLines) {
        updates[giftLine.key] = giftToKeep && giftLine.key === giftToKeep.key ? 1 : 0;
      }

      await postCart('/cart/update.js', { updates });
      announceCartRefresh();
      scheduleRefresh(80);
    } catch (error) {
      const selector = document.querySelector('[data-caleo-free-gift-v2]');
      showStatus(selector, error instanceof Error ? error.message : 'Unable to correct your free gift.', true);
    } finally {
      isMutating = false;
      setCheckoutDisabled(false);
    }
  }

  async function removeGiftLines(giftLines) {
    if (isMutating || giftLines.length === 0) return;
    isMutating = true;
    setCheckoutDisabled(true);

    try {
      await postCart('/cart/update.js', {
        updates: Object.fromEntries(giftLines.map((item) => [item.key, 0])),
      });
      announceCartRefresh();
      scheduleRefresh(80);
    } catch (error) {
      const selector = document.querySelector('[data-caleo-free-gift-v2]');
      showStatus(selector, error instanceof Error ? error.message : 'Please remove the free gift before checkout.', true);
    } finally {
      isMutating = false;
      setCheckoutDisabled(false);
    }
  }

  async function refresh() {
    if (isMutating || products.length === 0) return;
    const { root, body } = getMountPoint();
    if (!root || !body) return;

    try {
      const cart = await fetchCart();
      const giftLines = getGiftLines(cart);
      const eligible = isEligible(cart);
      root.classList.toggle('caleo-free-gift-eligible', eligible);

      if (!eligible) {
        renderSelector(cart);
        if (giftLines.length > 0) await removeGiftLines(giftLines);
        return;
      }

      const validGiftLines = giftLines.filter((item) => giftVariantIds.has(Number(item.variant_id)));
      const needsNormalization =
        giftLines.length > 1 ||
        validGiftLines.length !== giftLines.length ||
        giftLines.some((item) => Number(item.quantity) !== 1);

      if (needsNormalization) {
        await normalizeGiftLines(giftLines);
        return;
      }

      if (giftLines.length === 0) {
        const defaultProduct = products[0];
        const defaultVariant = preferredVariant(defaultProduct);
        const selector = renderSelector(cart, defaultVariant.id);
        await replaceGift(defaultVariant.id, selector, []);
        return;
      }

      renderSelector(cart);
    } catch (error) {
      const selector = root.querySelector('[data-caleo-free-gift-v2]');
      showStatus(selector, error instanceof Error ? error.message : 'Unable to refresh your free gift.', true);
    }
  }

  function scheduleRefresh(delay = 120) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refresh, delay);
  }

  document.addEventListener('theme:cart:updated', () => scheduleRefresh());
  document.addEventListener('cart:updated', () => scheduleRefresh());

  const appObserver = new MutationObserver((mutations) => {
    const relevantMutation = mutations.some((mutation) => {
      const target = mutation.target;
      if (target instanceof Element && target.matches('[data-rapi-cart-body]')) return true;

      return Array.from(mutation.addedNodes).some(
        (node) =>
          node instanceof Element &&
          (node.matches('#rapi-cart-drawer-app, [data-rapi-cart-body]') ||
            node.querySelector('#rapi-cart-drawer-app, [data-rapi-cart-body]'))
      );
    });
    if (relevantMutation) scheduleRefresh();
  });

  appObserver.observe(document.documentElement, { childList: true, subtree: true });
  scheduleRefresh();
}
